import React from 'react'
import { css } from '@emotion/core'
import { baseUrl } from '@fs/zion-config'
import { LayoutBand, colors, Grid, H2, Separator, Paragraph, Button, SwipeableGrid, usePageWidth } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'

import PersonCard, { PersonCardSkeleton } from './PersonCard'
import useTree from '../../../hooks/use-tree'

// Since SwipeableGrid only has a white gradient by default, we need to override it for gray backgrounds
const gradientMap = {
  [colors.gray02]: '--gradient-color-base: 97%;',
}

const sectionCss = (backgroundColor) => css`
  ${gradientMap[backgroundColor] || ''}
`

const SeeMoreButton = ({ isMobile, surname, t }) => (
  <Button
    emphasis="medium"
    color="gray"
    external
    linkName="link: see-more: click"
    fullWidth={isMobile}
    to={`${baseUrl}/search/discovery/results/?tab=preview&q.surname=${surname}`}
  >
    {t('apac.section-templates.surname-tree-person-section.button', 'See More')}
  </Button>
)

export default function SurnameTreePersonSection({
  surname,
  backgroundColor = colors.gray02,
  subtitle = '',
  showFades,
}) {
  const { t } = useTranslation()
  const atWidth = usePageWidth()
  const isMobile = atWidth({ default: true, lg: false })
  const { data, isLoading } = useTree({
    search: { 'q.surname': surname, 'm.queryRequireDefault': 'on' },
    pagination: { page: 1, results: 4 },
  })

  // Don't render the section if there's no data and not loading
  if (!isLoading && !data?.results?.length) {
    return null
  }

  const subtitleText =
    subtitle ||
    t(
      'apac.section-templates.surname-tree-person-section.description',
      'See FamilySearch family records with the {surname} name.',
      { surname }
    )

  return (
    <LayoutBand marginY={isMobile ? 'xl' : 'xxl'} color={backgroundColor} css={sectionCss(backgroundColor)}>
      <Grid>
        <div columns={isMobile ? 12 : 4} alignY="middle">
          <H2 size="H3" supplementary balanced centered={isMobile}>
            {t('apac.section-templates.surname-tree-person-section.title', 'Discover {surname} Family Members', {
              surname,
            })}
          </H2>
          <Separator size="xs" />
          <Paragraph centered={isMobile} size={isMobile ? 'sm' : 'md'}>
            {subtitleText}
          </Paragraph>
          <Separator size="sm" />
          {!isMobile && <SeeMoreButton surname={surname} t={t} />}
        </div>
        <SwipeableGrid
          columnCount={atWidth({ default: 1, md: 2, lg: 3 })}
          gutters={isMobile ? 'xs' : 'sm'}
          columns={isMobile ? 12 : 8}
          hideFades={!showFades}
        >
          {isLoading
            ? Array.from({ length: 4 }, (_, index) => <PersonCardSkeleton key={`skeleton-${index}`} />)
            : data?.results?.map((person) => <PersonCard key={person.identifier} person={person} />)}
        </SwipeableGrid>
      </Grid>
      {isMobile && (
        <>
          <Separator size="md" />
          <SeeMoreButton isMobile surname={surname} t={t} />
        </>
      )}
    </LayoutBand>
  )
}
